import { getAuth, type Auth, type AuthData } from 'home-assistant-js-websocket'

const TOKEN_KEY = 'ha_tokens'
const HASS_URL = import.meta.env.VITE_HA_URL ?? 'https://ha.uctaudio.com'

function loadTokens(): AuthData | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY)
    return raw ? (JSON.parse(raw) as AuthData) : null
  } catch {
    return null
  }
}

function saveTokens(tokens: AuthData | null): void {
  try {
    if (tokens) localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* private mode / storage disabled — we simply can't persist the session */
  }
}

export function clearTokens(): void {
  saveTokens(null)
}

/**
 * Resolve an Auth object. If we hold saved tokens it returns immediately; if the
 * page came back from HA's login (`?auth_callback=1`) it completes the exchange;
 * otherwise it throws so the caller can trigger a redirect to HA's login page.
 */
export async function resolveAuth(): Promise<Auth> {
  const auth = await getAuth({
    hassUrl: HASS_URL,
    saveTokens,
    loadTokens: async () => loadTokens(),
  })
  // strip the auth_callback query params so a refresh doesn't re-trigger the exchange
  if (location.search.includes('auth_callback=1')) {
    history.replaceState(null, '', location.pathname)
  }
  return auth
}

/** Redirect the browser to HA's login screen (returns here afterwards). */
export async function redirectToLogin(): Promise<void> {
  await getAuth({ hassUrl: HASS_URL, saveTokens })
}

export { HASS_URL }
